/**
 * Minicart Static Upsell
 *
 * Injects a hand-picked product (chosen in the theme customizer) into the
 * minicart with a quick-add-to-cart button. Replaces the legacy dynamic
 * "Related Product" feature.
 *
 * Pattern mirrors minicart-hero-announcement.js and minicart-subscription-handler.js:
 *  1. Read config from <script id="minicart-static-upsell-data"> (rendered by minicart.liquid)
 *  2. Watch the minicart container for open/update events
 *  3. Inject the card above the shipping notice / subtotal
 *  4. Wire the button to /cart/add.js and dispatch cart:updated so the rest
 *     of the minicart re-renders
 */

(function () {
  'use strict';

  var CONFIG_ELEMENT_ID = 'minicart-static-upsell-data';
  var UPSELL_CLASS = 'minicart__static-upsell';

  // Anchors at the BOTTOM of the cart, in priority order. We insert BEFORE
  // the first matching element so the upsell sits between the line items
  // and the totals/checkout area. We deliberately avoid the shipping notice
  // — the theme moves that to the TOP of the items list, which is the wrong
  // position for an upsell.
  var INJECT_BEFORE_SELECTORS = [
    '.minicart__subtotal',
    '.minicart__totals',
    '.minicart__footer',
    '.minicart__bottom',
    '[class*="minicart__subtotal"]',
    '[class*="minicart__footer"]',
    '[class*="minicart__bottom"]'
  ];

  // ---------- helpers ----------

  function getConfig() {
    var el = document.getElementById(CONFIG_ELEMENT_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[minicart-static-upsell] failed to parse config', e);
      return null;
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (m) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[m];
    });
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  function isProductInCart(productId, cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    return cart.items.some(function (i) {
      return i.product_id === productId;
    });
  }

  function findInjectionTarget() {
    for (var i = 0; i < INJECT_BEFORE_SELECTORS.length; i++) {
      var el = document.querySelector(INJECT_BEFORE_SELECTORS[i]);
      if (el) return el;
    }
    return null;
  }

  // ---------- rendering ----------

  function findVariantById(variants, id) {
    if (!Array.isArray(variants)) return null;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(id)) return variants[i];
    }
    return null;
  }

  function renderPriceHtml(variant, showPrice) {
    if (!showPrice || !variant || !variant.price_formatted) return '';
    var hasCompare =
      variant.compare_at_price &&
      variant.compare_at_price > variant.price &&
      variant.compare_at_price_formatted;
    var compare = hasCompare
      ? ' <s class="' + UPSELL_CLASS + '__compare">' +
        escapeHtml(variant.compare_at_price_formatted) +
        '</s>'
      : '';
    return (
      '<p class="' + UPSELL_CLASS + '__price">' +
      escapeHtml(variant.price_formatted) +
      compare +
      '</p>'
    );
  }

  function buildCard(config) {
    var product = config.product;
    var variants = Array.isArray(product.variants) ? product.variants : [];
    if (variants.length === 0) return null;

    var selected =
      findVariantById(variants, product.selected_variant_id) ||
      variants.filter(function (v) { return v.available; })[0] ||
      variants[0];
    if (!selected) return null;

    var hasMultipleVariants =
      variants.length > 1 && product.has_only_default_variant !== true;

    var card = document.createElement('div');
    card.className = UPSELL_CLASS;
    card.setAttribute('data-product-id', String(product.id));
    card.setAttribute('data-variant-id', String(selected.id));

    var imageHtml = product.featured_image
      ? '<img class="' + UPSELL_CLASS + '__image" src="' + escapeHtml(product.featured_image) +
        '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="64" height="64">'
      : '';

    var subheadingHtml = config.subheading
      ? '<p class="' + UPSELL_CLASS + '__subheading">' + escapeHtml(config.subheading) + '</p>'
      : '';

    var variantLabelText =
      config.variant_label && config.variant_label.length
        ? config.variant_label
        : (product.options && product.options[0]) || '';

    var variantPickerHtml = '';
    if (hasMultipleVariants) {
      var optionsHtml = variants
        .map(function (v) {
          var label = v.title + (v.available ? '' : ' — Sold out');
          return (
            '<option value="' + escapeHtml(v.id) + '"' +
            (String(v.id) === String(selected.id) ? ' selected' : '') +
            (v.available ? '' : ' disabled') +
            '>' + escapeHtml(label) + '</option>'
          );
        })
        .join('');
      var labelHtml = variantLabelText
        ? '<label class="' + UPSELL_CLASS + '__variant-label" for="' + UPSELL_CLASS + '-select">' +
          escapeHtml(variantLabelText) + '</label>'
        : '';
      variantPickerHtml =
        '<div class="' + UPSELL_CLASS + '__variant-row">' +
          labelHtml +
          '<div class="' + UPSELL_CLASS + '__select-wrap">' +
            '<select id="' + UPSELL_CLASS + '-select" class="' + UPSELL_CLASS + '__select">' +
              optionsHtml +
            '</select>' +
          '</div>' +
        '</div>';
    }

    var buttonLabel = escapeHtml(config.button_text || 'Add');
    var buttonDisabledAttr = selected.available ? '' : ' disabled';
    var buttonText = selected.available ? buttonLabel : 'Sold out';

    card.innerHTML =
      '<style>' +
      '.' + UPSELL_CLASS + '{' +
        'margin:0 30px 16px;padding:14px;' +
        'border:1px solid var(--text-color,#003452);border-radius:8px;' +
        'background:#fff;font-family:inherit;' +
      '}' +
      '.' + UPSELL_CLASS + '__subheading{' +
        'margin:0 0 10px;font-size:11px;font-weight:700;letter-spacing:.5px;' +
        'text-transform:uppercase;color:var(--text-color,#003452);' +
      '}' +
      '.' + UPSELL_CLASS + '__inner{display:flex;align-items:center;gap:12px;}' +
      '.' + UPSELL_CLASS + '__image{' +
        'width:64px;height:64px;object-fit:cover;border-radius:6px;flex-shrink:0;background:#f3f3f3;' +
      '}' +
      '.' + UPSELL_CLASS + '__info{flex:1 1 auto;min-width:0;}' +
      '.' + UPSELL_CLASS + '__title{' +
        'margin:0 0 4px;font-size:13px;font-weight:600;line-height:1.3;' +
        'color:var(--text-color,#003452);' +
        'overflow:hidden;text-overflow:ellipsis;display:-webkit-box;' +
        '-webkit-line-clamp:2;-webkit-box-orient:vertical;' +
      '}' +
      '.' + UPSELL_CLASS + '__price{margin:0;font-size:13px;color:var(--text-color,#003452);}' +
      '.' + UPSELL_CLASS + '__compare{margin-left:6px;opacity:.6;font-weight:400;}' +
      '.' + UPSELL_CLASS + '__variant-row{margin-top:10px;display:flex;flex-direction:column;gap:4px;}' +
      '.' + UPSELL_CLASS + '__variant-label{' +
        'font-size:11px;font-weight:600;letter-spacing:.3px;text-transform:uppercase;' +
        'color:var(--text-color,#003452);opacity:.8;' +
      '}' +
      '.' + UPSELL_CLASS + '__select-wrap{position:relative;}' +
      '.' + UPSELL_CLASS + '__select-wrap::after{' +
        'content:"";position:absolute;top:50%;right:12px;width:8px;height:8px;' +
        'border-right:2px solid var(--text-color,#003452);' +
        'border-bottom:2px solid var(--text-color,#003452);' +
        'transform:translateY(-75%) rotate(45deg);pointer-events:none;' +
      '}' +
      '.' + UPSELL_CLASS + '__select{' +
        'width:100%;padding:8px 32px 8px 12px;' +
        'border:1px solid var(--text-color,#003452);border-radius:6px;' +
        'background:#fff;color:var(--text-color,#003452);' +
        'font-size:13px;font-family:inherit;cursor:pointer;' +
        'appearance:none;-webkit-appearance:none;-moz-appearance:none;' +
      '}' +
      '.' + UPSELL_CLASS + '__button{' +
        'flex-shrink:0;padding:8px 14px;border:1px solid var(--text-color,#003452);' +
        'background:var(--text-color,#003452);color:#fff;font-size:11px;font-weight:700;' +
        'text-transform:uppercase;letter-spacing:.5px;border-radius:6px;cursor:pointer;' +
        'transition:opacity .2s;' +
      '}' +
      '.' + UPSELL_CLASS + '__button:hover{opacity:.85;}' +
      '.' + UPSELL_CLASS + '__button[disabled]{opacity:.5;cursor:not-allowed;}' +
      '</style>' +
      subheadingHtml +
      '<div class="' + UPSELL_CLASS + '__inner">' +
        imageHtml +
        '<div class="' + UPSELL_CLASS + '__info">' +
          '<p class="' + UPSELL_CLASS + '__title">' + escapeHtml(product.title) + '</p>' +
          renderPriceHtml(selected, config.show_price) +
        '</div>' +
        '<button type="button" class="' + UPSELL_CLASS + '__button" data-variant-id="' +
          escapeHtml(selected.id) + '"' + buttonDisabledAttr + '>' +
          buttonText +
        '</button>' +
      '</div>' +
      variantPickerHtml;

    return card;
  }

  function wireVariantPicker(card, config) {
    var select = card.querySelector('.' + UPSELL_CLASS + '__select');
    if (!select) return;

    var button = card.querySelector('.' + UPSELL_CLASS + '__button');
    var infoPriceEl = card.querySelector('.' + UPSELL_CLASS + '__price');
    var infoBlock = card.querySelector('.' + UPSELL_CLASS + '__info');
    var imageEl = card.querySelector('.' + UPSELL_CLASS + '__image');

    select.addEventListener('change', function () {
      var newVariant = findVariantById(config.product.variants, select.value);
      if (!newVariant) return;

      card.setAttribute('data-variant-id', String(newVariant.id));
      if (button) {
        button.setAttribute('data-variant-id', String(newVariant.id));
        if (newVariant.available) {
          button.disabled = false;
          button.textContent = config.button_text || 'Add';
        } else {
          button.disabled = true;
          button.textContent = 'Sold out';
        }
      }

      // Update price
      var priceHtml = renderPriceHtml(newVariant, config.show_price);
      if (infoPriceEl) {
        infoPriceEl.outerHTML = priceHtml || '';
      } else if (priceHtml && infoBlock) {
        infoBlock.insertAdjacentHTML('beforeend', priceHtml);
      }

      // Swap image if the variant has its own
      if (imageEl && newVariant.featured_image) {
        imageEl.src = newVariant.featured_image;
      }
    });
  }

  // ---------- behavior ----------

  function handleAdd(button, variantId) {
    if (button.disabled) return;
    var original = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Add to cart failed (' + res.status + ')');
        return res.json();
      })
      .then(function () {
        return fetchCart();
      })
      .then(function (cart) {
        // Notify the minicart framework that the cart changed.
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));

        // The bundled minicart in custom.js uses `_.dispatch('getCart')`.
        // Trigger it if the global is available.
        try {
          if (typeof window._ !== 'undefined' && typeof window._.dispatch === 'function') {
            window._.dispatch('getCart');
          }
        } catch (e) { /* no-op */ }

        button.textContent = 'Added ✓';
        setTimeout(function () {
          button.textContent = original;
          button.disabled = false;
        }, 1200);
      })
      .catch(function (err) {
        console.error('[minicart-static-upsell]', err);
        button.textContent = 'Error';
        setTimeout(function () {
          button.textContent = original;
          button.disabled = false;
        }, 1500);
      });
  }

  function inject(config) {
    if (!config || !config.enabled || !config.product) return;

    var doInject = function (cart) {
      // Final dedup IMMEDIATELY before insertion. This is critical: when
      // hide_if_in_cart is on, doInject is async, so multiple retries
      // can race past an earlier dedup. Checking here guarantees only
      // one card exists in the DOM at a time.
      var existing = document.querySelectorAll('.' + UPSELL_CLASS);
      existing.forEach(function (el) { el.remove(); });

      if (config.hide_if_in_cart && isProductInCart(config.product.id, cart)) return;

      var target = findInjectionTarget();
      if (!target) return;

      var card = buildCard(config);
      if (!card) return;

      target.parentNode.insertBefore(card, target);

      wireVariantPicker(card, config);

      var btn = card.querySelector('.' + UPSELL_CLASS + '__button');
      if (btn) {
        btn.addEventListener('click', function () {
          handleAdd(btn, btn.getAttribute('data-variant-id'));
        });
      }
    };

    if (config.hide_if_in_cart) {
      fetchCart().then(doInject).catch(function () { doInject(null); });
    } else {
      doInject(null);
    }
  }

  function attemptInjection() {
    var config = getConfig();
    if (!config || !config.enabled) return;
    // Two retries: one quick (catches synchronous renders), one a beat later
    // (catches the bundled JS's async re-render after a cart change). The
    // dedup inside inject() makes additional retries unnecessary and risky.
    setTimeout(function () { inject(config); }, 120);
    setTimeout(function () { inject(config); }, 500);
  }

  function observeMinicart() {
    var container = document.querySelector('.minicart__container, #minicart');
    if (!container) {
      setTimeout(observeMinicart, 500);
      return;
    }

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName === 'class') {
          var c = m.target;
          if (
            c.classList.contains('minicart__container--open') ||
            c.classList.contains('is-open')
          ) {
            attemptInjection();
          }
        }
      });
    });

    observer.observe(container, { attributes: true, attributeFilter: ['class'] });

    // Also try once on first load in case it's already open
    attemptInjection();
  }

  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeMinicart);
    } else {
      observeMinicart();
    }

    document.addEventListener('cart:updated', attemptInjection);
    document.addEventListener('cart:refresh', attemptInjection);
    document.addEventListener('minicart:opened', attemptInjection);
    document.addEventListener('minicart:updated', attemptInjection);
  }

  init();
})();